import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { toggleDebugMenu } from '@/state/features/app';
import Modal from '@/components/Modal';
import { useTheme } from '@/theme';
import * as arise from '@/arise/api';
import { clearProfileV2FromLocalStorage } from '@/utils/localProfileV2';
import { clearLocalVideoProgressFromStorage } from '@/utils/localVideoProgress';

import * as styles from './styles.module.scss';

export default function AriseDebugMenu() {
    const dispatch = useAppDispatch();
    const isDebugMenuOpen = useAppSelector((state) => state.app.isDebugMenuOpen);
    const currentUserProfileV2 = useAppSelector((state) => state.profile.currentUserProfileV2);
    const location = useAppSelector((state) => state.app.location);
    const theme = useTheme();
    const [error, setError] = useState<string | null>();
    const [iframeEventName, setIframeEventName] = useState('');
    const [iframeEventPayload, setIframeEventPayload] = useState('');

    const handleSimulateIframeEvent = () => {
        setError(null);
        try {
            // Parse payload if it's JSON, otherwise use as string
            let parsedPayload = iframeEventPayload;
            try {
                parsedPayload = JSON.parse(iframeEventPayload);
            } catch {
                // Keep as string if not valid JSON
            }

            console.log('Simulating iFrame message event:', {
                type: iframeEventName,
                payload: parsedPayload,
            });

            // Simulate a message event from an iframe (handled by AriseBridgeHost)
            const messageEvent = new MessageEvent('message', {
                data: {
                    type: iframeEventName,
                    payload: parsedPayload,
                },
                origin: window.location.origin,
            });
            window.dispatchEvent(messageEvent);

            console.log(`MessageEvent "${iframeEventName}" dispatched with payload:`, parsedPayload);
        } catch (err) {
            setError('Failed to simulate event: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    const handleSimulateIframeEventAfterDelay = () => {
        dispatch(toggleDebugMenu());
        setTimeout(() => {
            handleSimulateIframeEvent();
        }, 2000);
    };

    const handleClearLocalProfile = () => {
        setError(null);
        try {
            clearProfileV2FromLocalStorage();
        } catch (err) {
            setError('Failed to clear local profile');
        }
    };

    const handleClearRemoteProfile = async () => {
        setError(null);
        try {
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
        } catch (err) {
            setError('Failed to clear remote profile');
        }
    };

    const handleClearProfile = async () => {
        setError(null);
        try {
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
            clearProfileV2FromLocalStorage();
        } catch (err) {
            setError('Failed to clear profile');
        }
    };

    const handleClearLocalVideoData = async () => {
        setError(null);
        try {
            clearLocalVideoProgressFromStorage();
        } catch (err) {
            setError('Failed to clear local video data');
        }
    };

    const handleClearLocalAuthToken = () => {
        setError(null);
        try {
            window.localStorage.removeItem('ARISEENGINE_AUTHTOKEN');
        } catch (err) {
            setError('Failed to clear auth token');
        }
    };

    const handleClearAllLocalStorage = () => {
        setError(null);
        try {
            window.localStorage.clear();
        } catch (err) {
            setError('Failed to clear local storage');
        }
    };

    if (!hasAriseDebugMenu(theme.customJSON)) {
        return null;
    }

    const getArisewareData = () => {
        if (!location?.latestArisewareBuildURL) return 'No Ariseware';
        try {
            const arisewareVersionDateTime = location.latestArisewareBuildURL.match(/\/(\d+)\//)[1];
            const dateVersion = new Date(parseInt(arisewareVersionDateTime)).toLocaleString();
            return `${dateVersion} (${arisewareVersionDateTime})`;
        } catch (error) {
            return location?.latestArisewareBuildURL || 'No Ariseware';
        }
    };

    return (
        <Modal
            title="Debug Menu"
            isVisible={isDebugMenuOpen}
            onClose={() => dispatch(toggleDebugMenu())}
            hasCloseButton={true}
            modalClassname={styles.AriseDebugMenu}
            zIndex={2000}
            canScroll
            leaveSpaceAboveAndBelow={false}
        >
            {error && (
                <div className={styles.errorMessage}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            <section>
                <h3>System Info</h3>
                <ul>
                    <li>
                        <strong>Arise Frontend version: </strong>
                        {process.env.PUBLIC_ENV !== 'dev' ? process.env.GIT_TAG : process.env.PUBLIC_ENV}
                    </li>
                    <li>
                        <strong>Ariseware version:</strong> {getArisewareData()}
                    </li>
                    <li>
                        <strong>User ID:</strong> {currentUserProfileV2?.userId}
                    </li>
                    <li>
                        <strong>Auth token:</strong> {window.localStorage.getItem('ARISEENGINE_AUTHTOKEN') || ''}
                    </li>
                </ul>
            </section>
            <section>
                <h3>Profile Actions</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalProfile}>Clear Local Profile</button>
                <button onClick={handleClearRemoteProfile}>Clear Remote Profile</button>
                <button onClick={handleClearProfile}>Clear All Profile Data</button>
            </section>
            <section>
                <h3>Video</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalVideoData}>Clear Local Video Data</button>
            </section>
            <section>
                <h3>User</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalAuthToken}>Clear Local Auth Token</button>
                <button onClick={handleClearAllLocalStorage}>Clear All Local Storage Data</button>
            </section>
            <section>
                <h3>Simulate event from iFrame</h3>
                <div className={styles.formGroup}>
                    <label>Event name:</label>
                    <input
                        type="text"
                        placeholder="e.g. open-url"
                        value={iframeEventName}
                        onChange={(e) => setIframeEventName(e.target.value)}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Payload detail (JSON or string):</label>
                    <textarea
                        placeholder="e.g. https://www.google.com"
                        value={iframeEventPayload}
                        onChange={(e) => setIframeEventPayload(e.target.value)}
                        rows={3}
                    />
                </div>
                <button onClick={handleSimulateIframeEvent}>Send</button>
                <button onClick={handleSimulateIframeEventAfterDelay}>Close Debug, wait 5 seconds and then Send</button>
            </section>
        </Modal>
    );
}

export function hasAriseDebugMenu(themeCustomJSON: any) {
    // Use hasDebugMenu value if exists, otherwise default to showing for non prod envs
    return typeof themeCustomJSON?.hasDebugMenu === 'boolean'
        ? themeCustomJSON.hasDebugMenu
        : process.env.PUBLIC_ENV !== 'prod';
}
